import { useCallback, useEffect, useRef, useState } from 'react';

/** Where an autosaved value is in its save cycle. `invalid` = the draft failed
 *  `validate` and was held back (nothing is sent until it's fixed). */
export type AutosaveStatus = 'idle' | 'saving' | 'saved' | 'error' | 'invalid';

export interface AutosaveOptions<T> {
  /** The current draft. Any change (by deep value) schedules a save. */
  value: T;
  /** Persist `v`. Resolve with the server's echo to re-seed the baseline, or
   *  void to take `v` as saved. Throw to surface an error. */
  save: (v: T) => Promise<T | void>;
  /** Called with the server echo after a successful save. */
  onSaved?: (saved: T) => void;
  /** Debounce after the last change, in ms. Toggles/selects can pass 0. */
  delay?: number;
  /** false while the initial value is still loading — nothing is saved and the
   *  baseline follows `value`. */
  enabled?: boolean;
  /** Return an error message to hold the save back, or null when valid. */
  validate?: (v: T) => string | null;
}

export interface AutosaveResult {
  status: AutosaveStatus;
  /** Error from the last failed save, or the validation message. */
  error: string | null;
  /** Save the pending draft now (skips the debounce). */
  flush: () => void;
  /** Retry after an error. */
  retry: () => void;
}

const same = (a: unknown, b: unknown) => JSON.stringify(a) === JSON.stringify(b);

/**
 * Debounced save-on-change for a settings draft. Saves are serialised: a change
 * made while a save is in flight is saved right after it lands. A pending save
 * is flushed (not dropped) when the component unmounts, so leaving the page
 * straight after an edit still persists it. On failure the draft is kept as
 * typed and the status becomes `error` until the next change or `retry`.
 */
export function useAutosave<T>({ value, save, onSaved, delay = 700, enabled = true, validate }: AutosaveOptions<T>): AutosaveResult {
  const [status, setStatus] = useState<AutosaveStatus>('idle');
  const [error, setError] = useState<string | null>(null);

  const baseline = useRef<T>(value);
  const latest = useRef<T>(value);
  const inFlight = useRef(false);
  const timer = useRef<number | null>(null);
  const mounted = useRef(true);
  const wasEnabled = useRef(enabled);
  const saveRef = useRef(save);
  const onSavedRef = useRef(onSaved);
  const validateRef = useRef(validate);
  saveRef.current = save;
  onSavedRef.current = onSaved;
  validateRef.current = validate;
  latest.current = value;
  // While disabled (still loading) — and on the render it becomes enabled — the
  // value IS the saved state, so it becomes the baseline. Idempotent, so safe in
  // render even under StrictMode's double render.
  if (!enabled || !wasEnabled.current) baseline.current = value;
  wasEnabled.current = enabled;

  const run = useCallback(async () => {
    if (timer.current !== null) {
      window.clearTimeout(timer.current);
      timer.current = null;
    }
    if (inFlight.current) return; // the in-flight save re-runs when it lands
    const draft = latest.current;
    if (same(draft, baseline.current)) return;
    const invalid = validateRef.current?.(draft) ?? null;
    if (invalid) {
      if (mounted.current) {
        setStatus('invalid');
        setError(invalid);
      }
      return;
    }
    inFlight.current = true;
    if (mounted.current) {
      setStatus('saving');
      setError(null);
    }
    try {
      const echo = await saveRef.current(draft);
      const saved = (echo === undefined ? draft : echo) as T;
      baseline.current = saved;
      onSavedRef.current?.(saved);
      if (mounted.current) setStatus('saved');
    } catch (e) {
      // Keep the draft; don't retry automatically (the next edit or retry() does).
      if (mounted.current) {
        setStatus('error');
        setError(e instanceof Error ? e.message : String(e));
      }
      inFlight.current = false;
      return;
    }
    inFlight.current = false;
    // Edits made while saving: save them now.
    if (!same(latest.current, draft)) void run();
  }, []);

  // Nothing is pending while disabled.
  useEffect(() => {
    if (!enabled && timer.current !== null) {
      window.clearTimeout(timer.current);
      timer.current = null;
    }
  }, [enabled]);

  const key = JSON.stringify(value);
  useEffect(() => {
    if (!enabled) return;
    if (same(latest.current, baseline.current)) {
      // Back to the saved value (e.g. undo) — cancel anything pending.
      if (timer.current !== null) window.clearTimeout(timer.current);
      timer.current = null;
      if (!inFlight.current) {
        setStatus((s) => (s === 'invalid' || s === 'error' ? 'idle' : s));
        setError(null);
      }
      return;
    }
    if (timer.current !== null) window.clearTimeout(timer.current);
    timer.current = window.setTimeout(() => void run(), delay);
  }, [key, enabled, delay, run]);

  // Fade "Saved" back to idle.
  useEffect(() => {
    if (status !== 'saved') return;
    const t = window.setTimeout(() => setStatus('idle'), 2500);
    return () => window.clearTimeout(t);
  }, [status]);

  // Flush a pending edit on unmount instead of dropping it.
  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
      if (timer.current !== null) {
        window.clearTimeout(timer.current);
        timer.current = null;
        void run();
      }
    };
  }, [run]);

  const flush = useCallback(() => void run(), [run]);
  return { status, error, flush, retry: flush };
}
